import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BookOpen, ChevronRight, Award, BookMarked, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';

const MappaCaeliLearningShowcase: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-amber-50/50 to-background dark:from-amber-950/20 dark:to-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-sm font-semibold mb-4">
              <GraduationCap className="w-4 h-4" />
              {t('mappaCaeliLearning.newFeature', 'NEW: Catholic Worldview')}
            </div>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
              {t('mappaCaeliLearning.showcaseTitle', 'Catholic Worldview')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('mappaCaeliLearning.showcaseDescription', 'Interactive modules with Magisterial citations, logic exercises, and guided reflection to master the Catholic worldview.')}
            </p>
          </div>

          {/* Main Content */}
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left: Feature highlights */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                  <BookMarked className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground mb-1">
                    {t('mappaCaeliLearning.feature1Title', 'Magisterial Citations')}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {t('mappaCaeliLearning.feature1Desc', 'Learn from papal encyclicals and Church documents with proper sourcing.')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                  <Award className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground mb-1">
                    {t('mappaCaeliLearning.feature2Title', 'Logic Exercises')}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {t('mappaCaeliLearning.feature2Desc', 'Test your understanding with interactive checkpoints.')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                  <BookOpen className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground mb-1">
                    {t('mappaCaeliLearning.feature3Title', 'Guided Reflection')}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {t('mappaCaeliLearning.feature3Desc', 'Personal journaling prompts to deepen your understanding.')}
                  </p>
                </div>
              </div>

              {/* Stats */}
              <div className="flex gap-6 pt-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">2+</div>
                  <div className="text-xs text-muted-foreground">{t('mappaCaeliLearning.modules', 'Modules')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">7</div>
                  <div className="text-xs text-muted-foreground">{t('mappaCaeliLearning.stepsPerModule', 'Steps Each')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">5+</div>
                  <div className="text-xs text-muted-foreground">{t('mappaCaeliLearning.papalDocs', 'Papal Docs')}</div>
                </div>
              </div>
            </div>

            {/* Right: Featured Module Card */}
            <div className="relative">
              <div className="bg-card border border-border rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 text-xs font-bold uppercase tracking-wider mb-3">
                  <span className="w-6 h-px bg-amber-600 dark:bg-amber-400"></span>
                  {t('mappaCaeliLearning.featuredModule', 'Featured Module')}
                </div>
                <h3 className="text-xl font-serif font-bold text-foreground mb-2">
                  {t('mappaCaeliLearning.module1Title', 'Truth Is Real and Knowable')}
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {t('mappaCaeliLearning.module1Desc', 'Explore how the Catholic Church understands truth, reason, and the capacity of the human mind to know reality.')}
                </p>
                
                {/* Module preview steps */}
                <div className="space-y-2 mb-6">
                  {['Scenario', 'Discovery', 'Three Pillars', 'Checkpoint', 'Authority', 'Reflection', 'Summary'].map((step, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                        i === 0 ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300' : 'bg-muted text-muted-foreground'
                      }`}>
                        {i + 1}
                      </div>
                      <span className={i === 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}>
                        {step}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-green-600 dark:text-green-400 font-semibold">
                    {t('mappaCaeliLearning.freeModule', '✓ Free to Start')}
                  </span>
                  <Link to="/mappa-caeli-learning">
                    <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                      {t('mappaCaeliLearning.startLearning', 'Start Learning')}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-amber-200/30 dark:bg-amber-800/20 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-amber-300/30 dark:bg-amber-700/20 rounded-full blur-xl"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MappaCaeliLearningShowcase;
